import { Typography } from '@material-ui/core';
import { Business } from '@material-ui/icons';
import {
  FormikTextField,
  SuspendedFormikPhoneField,
  useFormikEnhanced,
} from '@superdispatch/forms';
import { Column, Columns, Inline, Stack } from '@superdispatch/ui';
import { Button, DescriptionItem, TextBox } from '@superdispatch/ui-lab';
import { Form, FormikProvider } from 'formik';
import { useMemo } from 'react';
import { useInitialValues } from 'shared/form/AppFormik';
import { toFormikValidationSchema } from 'shared/form/FormSchema';
import { PoundIcon } from 'shared/icons';
import { Prompt } from 'shared/routing/NavigationBlock';
import { useCarrierInfo } from 'shared/modules/carrier-profile/data/CarrierInfoAPI';
import {
  VehiclesSetupCompanyInfoDTO,
  vehiclesSetupCompanyInfoSchema,
} from '../data/VehiclesDTO';

interface VehiclesSetupCompanyInfoStepProps {
  savedValues: undefined | VehiclesSetupCompanyInfoDTO;
  onContinue: (values: VehiclesSetupCompanyInfoDTO) => void;
}

export function VehiclesSetupCompanyInfoStep({
  savedValues,
  onContinue,
}: VehiclesSetupCompanyInfoStepProps) {
  const { data: carrierInfo } = useCarrierInfo();

  const initialValues = useInitialValues(
    vehiclesSetupCompanyInfoSchema,
    savedValues ?? {
      scac: '',
      contact: {
        name: carrierInfo?.contact_name,
        phone: carrierInfo?.phone_numbers,
        email: carrierInfo?.email,
      },
    },
  );
  const validationSchema = useMemo(
    () => toFormikValidationSchema(vehiclesSetupCompanyInfoSchema),
    [],
  );
  const formik = useFormikEnhanced({
    initialValues,
    validationSchema,
    onSubmit(values) {
      onContinue(values);
    },
  });

  return (
    <FormikProvider value={formik}>
      {/* Restored values reset `dirty`, but they are still unsaved wizard
          state — leaving the flow drops them just the same. */}
      <Prompt
        when={
          (formik.dirty || !!savedValues) && formik.status.type !== 'submitted'
        }
        message="Changes have not been saved. Leaving will result in unsaved changes being lost."
      />
      <Form aria-label="Company information form">
        <Stack space="large">
          <Stack space="xsmall">
            <Typography variant="h4">Safer FMCSA Record</Typography>
            <DescriptionItem icon={<Business />}>
              {carrierInfo?.name}
            </DescriptionItem>
            <DescriptionItem icon={<PoundIcon />}>
              {carrierInfo?.usdot_number}{' '}
              <Typography variant="caption" color="textSecondary">
                · USDOT
              </Typography>
            </DescriptionItem>
          </Stack>

          <FormikTextField
            name="scac"
            label={
              <Inline space="xxsmall">
                <TextBox>SCAC Code</TextBox>{' '}
                <TextBox color="secondary">Optional</TextBox>
              </Inline>
            }
            helperText="Your 4-letter Standard Carrier Alpha Code used in EDI transactions. You'll find it in your OEM/shipper onboarding docs or from NMFTA."
            fullWidth={true}
          />

          <Stack space="small">
            <Typography variant="h4">Contact</Typography>
            <FormikTextField
              name="contact.name"
              label="Full Name"
              fullWidth={true}
            />
            <Columns space="small" collapseBelow="tablet">
              <Column width="1/2">
                <SuspendedFormikPhoneField
                  name="contact.phone"
                  label="Phone Number"
                  fullWidth={true}
                  validate={(value, phoneService) =>
                    phoneService.validate(value, { required: false })
                  }
                />
              </Column>
              <Column width="1/2">
                <FormikTextField
                  name="contact.email"
                  label="Email"
                  fullWidth={true}
                />
              </Column>
            </Columns>
          </Stack>

          <Button type="submit" variant="primary" pending={formik.isSubmitting}>
            Continue to Connection Details
          </Button>
        </Stack>
      </Form>
    </FormikProvider>
  );
}
